import { InfoPage } from '@/components/info-page.js';
import { createTableRowSelectionCell, EntityTable, PinnedComponent } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { buildNameLikeSelectOptions } from '@/components/ui/multi-select-utils.js';
import { useAllMinimalOrgs } from '@/lib/api-call-hooks.js';
import { EmptyArray, getSchools } from '@/lib/api-calls.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { booleanFilterToSelectedValues, selectedValuesToBooleanFilter, usePaginatedFilters } from '@/lib/filtering.js';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing.js';
import { isAirAdmin } from '@airreading/common';
import { CreateSchool } from '@/routes/_layout/orgs/schools/-components/create-school.js';
import { distinct, formatPlainDate } from '@airreading/common';
import {
  getSchoolsSortableColumnSchema,
  GetSchoolsSortableColumn,
  getSchoolsSortOrderSchema,
  SchoolListRow,
  SchoolStatus,
  studentYearSchema,
  customSchoolStatusSchema,
  schoolStatusSchema,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { z } from 'zod';
import { airColumnHelper } from '@/lib/tables.js';
import { schoolTabs } from './-components/tabs.js';
import { useSorting } from '@/lib/sorting.js';
import { getStudentYearOptions } from '@/lib/student-utils.js';
import { ImportSchedulesFromV1 } from './-components/import-schedules-from-v1.js';
import { getDistrictColumn } from '@/lib/district-utils.js';
import { getCustomSchoolStatusOptions, schoolAccountStatusLabels } from '@/lib/school-utils.js';
import { Temporal } from 'temporal-polyfill';
import { RefreshSchoolsData } from './-components/refresh-schools-data.js';
import { EdlinkIdCode } from '@/components/alphanumeric-code.js';
import { buildBooleanSelectOptions } from '@/components/ui/multi-select-utils.js';

const schoolsSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional(),
  orgIds: z.array(z.string()).optional(),
  grades: z.array(studentYearSchema).optional(),
  customStatus: z.array(customSchoolStatusSchema).optional(),
  schoolStatuses: z.array(schoolStatusSchema).optional(),
  sortBy: getSchoolsSortOrderSchema.optional(),
  hasEdlinkId: z.boolean().optional(),
});

export const Route = createFileRoute('/_layout/orgs/schools/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchoolsIndex,
  validateSearch: schoolsSearchSchema,
});

const columnHelper = airColumnHelper<SchoolListRow>(getSchoolsSortableColumnSchema);
const columns = [
  createTableRowSelectionCell(columnHelper, { isPinned: true }),

  columnHelper.accessor('name', {
    header: 'Name',
    id: 'school-name',
    cell: ({ getValue, row, column }) => {
      return (
        <PinnedComponent pinned="left" column={column}>
          <Link
            to={'/orgs/schools/$schoolId/schedules'}
            params={{ schoolId: row.original.id }}
            className="text-blue-600"
          >
            {getValue()}
          </Link>
        </PinnedComponent>
      );
    },
  }),
  columnHelper.accessor('schoolStatus', {
    header: 'Status',
    id: 'school-account-status',
    cell: ({ getValue }) => schoolAccountStatusLabels[getValue()],
  }),
  columnHelper.accessor('org', {
    header: 'District',
    id: 'orgName',
    cell: ({ getValue, column }) => {
      const org = getValue();
      return (
        <PinnedComponent pinned="left" column={column}>
          {org ? getDistrictColumn({ orgId: org.id, orgName: org.name }) : <></>}
        </PinnedComponent>
      );
    },
  }),
  columnHelper.accessor('org.timeZone', {
    header: 'Time zone',
    id: 'org-timezone',
    cell: ({ getValue }) => {
      const timeZone = getValue();
      return timeZone || 'N/A';
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Practice date',
    id: 'practiceDate',
    cell: ({ getValue }) => {
      const { schedules } = getValue();
      const practiceDates = schedules
        .map((s) => s.practicePlainDate)
        .filter((date): date is Temporal.PlainDate => date !== null);

      if (practiceDates.length === 0) {
        return 'N/A';
      }

      return practiceDates.map((date) => <div key={date.toString()}>{formatPlainDate(date)}</div>);
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Diagnostic date',
    id: 'diagnosticDate',
    cell: ({ getValue }) => {
      const { schedules } = getValue();
      const diagnosticDates = schedules
        .map((s) => s.diagnosticPlainDate)
        .filter((date): date is Temporal.PlainDate => date !== null);

      if (diagnosticDates.length === 0) {
        return 'N/A';
      }

      return diagnosticDates.map((date) => <div key={date.toString()}>{formatPlainDate(date)}</div>);
    },
  }),
  columnHelper.accessor('schedules', {
    header: 'Schedule start date & end date',
    id: 'schedules',
    cell: ({ getValue }) => {
      // collapse schedules with the same start and end date
      const schedules = getValue().filter(distinct((s) => `${s.startPlainDate}-${s.endPlainDate}`));
      return schedules.length > 0
        ? schedules.map((s) => {
            return (
              <div key={s.id}>
                {formatPlainDate(s.startPlainDate)} - {formatPlainDate(s.endPlainDate)}
              </div>
            );
          })
        : 'No schedules';
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Total enrolled students',
    id: 'total-students',
    cell: ({ getValue }) => {
      const { totalStudents } = getValue();
      const hasStudentsCount = totalStudents && totalStudents > 0;

      return <div className="flex gap-2">{hasStudentsCount ? totalStudents : '0 students (enrolled)'}</div>;
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Number of future sessions',
    id: 'future-sessions',
    cell: ({ getValue }) => {
      const { totalFutureSessions } = getValue();
      const hasTotalFutureSessions = totalFutureSessions && totalFutureSessions.length > 0;
      const futureSessions = hasTotalFutureSessions ? totalFutureSessions.join(', ') : '0';

      return (
        <div className="flex gap-2">{futureSessions === '0' ? '0 number of future sessions' : futureSessions}</div>
      );
    },
  }),
  columnHelper.accessor('nextSessionPlainDate', {
    header: 'Next session date',
    id: 'nextSessionPlainDate',
    cell: ({ getValue }) => {
      const date = getValue();

      return <div className="flex gap-2">{date === null ? 'N/A' : formatPlainDate(date)}</div>;
    },
  }),
  columnHelper.accessor((row) => row, {
    header: 'Attendance percentage (last 14 days)',
    id: 'attendance-percentage',
    cell: ({ getValue }) => {
      const { attendancePercentLast14Days } = getValue();
      const hasAttendancePercentLast14Days = attendancePercentLast14Days && attendancePercentLast14Days > 0;

      return (
        <div className="flex gap-2">{hasAttendancePercentLast14Days ? `${attendancePercentLast14Days}%` : '0%'}</div>
      );
    },
  }),
  columnHelper.accessor('edlinkId', {
    header: 'Edlink ID',
    id: 'edlinkId',
    cell: ({ getValue }) => {
      const edlinkId = getValue();
      return edlinkId ? (
        <div className="flex items-center">
          <EdlinkIdCode code={edlinkId} wrapText={false} />
        </div>
      ) : (
        '-'
      );
    },
  }),
];

function SchoolsIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      orgIds: [[orgIds, activeOrgIds], setOrgIds],
      grades: [[grades, activeGrades], setGrades],
      customStatus: [[customStatus, activeCustomStatus], setCustomStatus],
      schoolStatuses: [[schoolStatuses, activeSchoolStatuses], setSchoolStatuses],
      hasEdlinkId: [[hasEdlinkId, activeHasEdlinkId], setHasEdlinkId],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'orgIds', 'grades', 'customStatus', 'schoolStatuses', 'hasEdlinkId']);
  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetSchoolsSortableColumn>([sortBy, didChangeSortBy]);

  const {
    data: response,
    error,
    refetch,
    isPending: isLoadingSchools,
  } = useQuery(
    getSchools({
      pageIndex,
      pageSize,
      filters: {
        query: activeQuery,
        orgIds: activeOrgIds,
        grades: activeGrades,
        customStatus: activeCustomStatus,
        schoolStatuses: activeSchoolStatuses,
        hasEdlinkId: activeHasEdlinkId,
      },
      sortBy,
    }),
  );
  const schools = useMemo(() => response?.schools || EmptyArray, [response]);
  const { data: allOrgs = [], isPending: isLoadingOrgs } = useAllMinimalOrgs();
  const orgOptions = allOrgs.map(buildNameLikeSelectOptions);

  useErrorToast(error, 'Error loading schools. Please try again.');

  const table = useReactTable({
    data: schools,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    rowCount: response?.total || 0,
    ...tableSortingOptions,
  });

  return (
    <InfoPage headerText="Schools" tabs={schoolTabs()}>
      <EntityTable
        table={table}
        hasError={!!error}
        showPagination
        isLoading={isLoadingSchools}
        errorState={<TableErrorState refetchAction={refetch} />}
        autoManagedColumnIds={['selection']}
        topBarRightComponents={
          <>
            <RefreshSchoolsData />
            <ImportSchedulesFromV1 />
            <CreateSchool />
          </>
        }
        filters={{
          filterLeftComponents: (
            <>
              <TableFilterInputField
                placeholder="Search schools by name"
                value={query ?? ''}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
              />
              <TableFilterDropdown
                title="District"
                options={orgOptions}
                selectedValues={orgIds || []}
                onChange={setOrgIds}
                isLoading={isLoadingOrgs}
              />
              <TableFilterDropdown
                title="Grade"
                options={getStudentYearOptions()}
                selectedValues={grades || []}
                onChange={setGrades}
              />
              <TableFilterDropdown
                title="School status"
                options={getCustomSchoolStatusOptions()}
                selectedValues={customStatus || []}
                onChange={setCustomStatus}
              />
              <TableFilterDropdown
                title="Active / Inactive"
                data-testid="schools-filter-dropdown-active-inactive"
                options={schoolStatusSchema.options.map((value) => ({
                  label: schoolAccountStatusLabels[value],
                  value,
                }))}
                selectedValues={schoolStatuses?.length ? schoolStatuses : [SchoolStatus.active]}
                onChange={(values) => setSchoolStatuses(values.length ? values : undefined)}
              />
              <TableFilterDropdown
                title="Edlink"
                options={buildBooleanSelectOptions()}
                variant="radio"
                selectedValues={booleanFilterToSelectedValues(hasEdlinkId)}
                onChange={(vals) => setHasEdlinkId(selectedValuesToBooleanFilter(vals))}
              />
              <TableFilterResetButton show onClick={resetFilters} />
            </>
          ),
        }}
      />
    </InfoPage>
  );
}
